import React from "react";
import { Link, Route } from "react-router-dom";

import CreateGame from "../components/CreateGame";
import createGameLeagueManagementLoader from "../components/CreateGame/loaders/createGameLeagueManagementLoader";
import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import RescheduleGame from "../components/RescheduleGame";
import RescheduleGameLeagueManagementLoader from "../components/RescheduleGame/loaders/rescheduleGameLeagueManagementLoader";
import { AUTH_LEVELS } from "../lib/globals";
import GameSummary from "../pages/Clubhouse/components/GameSummary";
import GameSummaryLoader from "../pages/Clubhouse/components/GameSummary/loaders/gameSummaryLeagueManagementLoader";
import ClubhouseLookingForPlayers from "../pages/Clubhouse/components/LookingForPlayers";
import LFPLoader from "../pages/Clubhouse/components/LookingForPlayers/loaders/lookingForPlayersLoader";
import ClubhouseTryouts from "../pages/Clubhouse/components/Tryouts";
import ClubhouseTryoutsLoader from "../pages/Clubhouse/components/Tryouts/loaders/tryoutsLoader";
import COI, { loader as COILoader } from "../pages/LeagueManagement/COI";
import COIApproval from "../pages/LeagueManagement/COIApproval";
import CoachRatings, {
    loader as CoachRatingsLoader,
} from "../pages/LeagueManagement/CoachRatings";
import Field, { loader as FieldLoader } from "../pages/LeagueManagement/Field";
import FieldsIndex, {
    loader as FieldsIndexLoader,
} from "../pages/LeagueManagement/FieldsIndex";
import GameLogs, {
    loader as GameLogsLoader,
} from "../pages/LeagueManagement/GameLogs";
import LeagueManagementIndex, {
    loader as LeagueManagementIndexLoader,
} from "../pages/LeagueManagement/Index";
import LeagueManagementLayout from "../pages/LeagueManagement/Layout";
import League, {
    loader as LeagueLoader,
} from "../pages/LeagueManagement/League";
import LeaguePlayoffSchedule, {
    loader as LeaguePlayoffScheduleLoader,
} from "../pages/LeagueManagement/LeaguePlayoffSchedule";
import LeagueRegistrations, {
    loader as LeagueRegistrationsLoader,
} from "../pages/LeagueManagement/LeagueRegistrations";
import LeagueSchedule, {
    loader as LeagueScheduleLoader,
} from "../pages/LeagueManagement/LeagueSchedule";
import LeagueManagementLeagueStandings, {
    loader as LeagueManagementStandingsLoader,
} from "../pages/LeagueManagement/LeagueStandings";
import LookingForPlayers, {
    loader as LookingForPlayersLoader,
} from "../pages/LeagueManagement/LookingForPlayers";
import Order, { loader as OrderLoader } from "../pages/LeagueManagement/Order";
import OrganizationIndex, {
    loader as OrganizationsIndexLoader,
} from "../pages/LeagueManagement/Organizations";
import Organization, {
    loader as OrganizationLoader,
} from "../pages/LeagueManagement/Organizations/components/Organization";
import PlacementResults, {
    loader as PlacementResultsLoader,
} from "../pages/LeagueManagement/PlacementResults";
import Product, {
    loader as ProductLoader,
} from "../pages/LeagueManagement/Product";
import ProductsIndex, {
    loader as ProductsLoader,
} from "../pages/LeagueManagement/Products";
import PromoCode, {
    loader as PromoCodeLoader,
} from "../pages/LeagueManagement/PromoCode";
import PromoCodes, {
    loader as PromoCodesLoader,
} from "../pages/LeagueManagement/PromoCodes";
import Tryouts, {
    loader as TryoutsLoader,
} from "../pages/LeagueManagement/Tryouts";
import UmpireRatings, {
    loader as UmpireRatingsLoader,
} from "../pages/LeagueManagement/UmpireRatings";
import PlayoffBracket, {
    loader as PlayoffBracketLoader,
} from "../pages/Leagues/PlayoffBracket";
import {
    IGame,
    ILeague,
    IOrder,
    IProduct,
    IPromoCode,
    ITeam,
} from "../types/api";

export default (
    <Route
        element={
            <RequireAuth allowedUserLevels={AUTH_LEVELS.leaguemanagement} />
        }
    >
        <Route
            path="league-management"
            handle={{
                crumb: () => (
                    <Link to="/league-management">League Management</Link>
                ),
            }}
            element={<LeagueManagementLayout />}
        >
            <Route
                index
                element={<LeagueManagementIndex />}
                loader={LeagueManagementIndexLoader}
                handle={{
                    header: () => "League Management",
                }}
            />
            <Route path="league">
                <Route
                    path=":leagueId/edit"
                    element={<League />}
                    loader={LeagueLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ leagueId }: { leagueId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/league/edit/${leagueId}`}
                                >
                                    Edit League
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path=":leagueId/schedule"
                    element={<LeagueSchedule />}
                    loader={LeagueScheduleLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ leagueId }: { leagueId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/league/${leagueId}/schedule`}
                                >
                                    League Schedule
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                >
                    <Route
                        path="create-game"
                        element={<CreateGame />}
                        loader={createGameLeagueManagementLoader}
                        handle={{
                            crumb: ({ leagueId }: { leagueId: string }) => {
                                return (
                                    <Link
                                        to={`/league-management/league/${leagueId}/schedule`}
                                    >
                                        League Schedule
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                    <Route
                        path="reschedule-game/:gameId"
                        element={<RescheduleGame />}
                        loader={RescheduleGameLeagueManagementLoader}
                        handle={{
                            crumb: ({
                                leagueId,
                                gameId,
                            }: {
                                leagueId: string;
                                gameId: string;
                            }) => {
                                return (
                                    <Link
                                        to={`/league-management/league/${leagueId}/reschedule-game/${gameId}`}
                                    >
                                        Reschedule Game
                                    </Link>
                                );
                            },
                            header: ({ header }: { header: string }) => header,
                        }}
                    />
                </Route>
                <Route
                    path=":leagueId/standings"
                    element={<LeagueManagementLeagueStandings />}
                    loader={LeagueManagementStandingsLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ leagueId }: { leagueId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/league/${leagueId}/standings`}
                                >
                                    League Standings
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path=":leagueId/playoffs"
                    element={<PlayoffBracket />}
                    loader={PlayoffBracketLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ leagueId }: { leagueId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/league/${leagueId}/standings`}
                                >
                                    League Playoff Bracket
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path=":leagueId/schedule/game/:gameId/summary"
                    element={<GameSummary />}
                    loader={GameSummaryLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({
                            leagueId,
                            gameId,
                        }: {
                            leagueId: ILeague["id"];
                            gameId: IGame["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/league-management/league/${leagueId}/game/${gameId}/summary`}
                                >
                                    Game Summary
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path=":leagueId/playoffSchedule"
                    element={<LeaguePlayoffSchedule />}
                    loader={LeaguePlayoffScheduleLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: ({ leagueId }: { leagueId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/league/${leagueId}/playoffSchedule`}
                                >
                                    League Schedule
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="add"
                    element={<League />}
                    loader={LeagueLoader}
                    shouldRevalidate={() => true}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/league-management/league/add`}>
                                    Add League
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="placement-results"
                element={<PlacementResults />}
                loader={PlacementResultsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/placement-results`}>
                                Placement Results
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            />
            <Route
                path="organizations"
                element={<OrganizationIndex />}
                loader={OrganizationsIndexLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/organizations`}>
                                Organizations
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            >
                <Route
                    path="edit/:orgId"
                    element={<Organization />}
                    loader={OrganizationLoader}
                    handle={{
                        crumb: ({ orgId }: { orgId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/organizations/edit/${orgId}`}
                                >
                                    Edit Organization
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="add"
                    element={<Organization />}
                    loader={OrganizationLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link
                                    to={`/league-management/organizations/add`}
                                >
                                    Add Organization
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="fields"
                element={<FieldsIndex />}
                loader={FieldsIndexLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/fields`}>
                                Field List
                            </Link>
                        );
                    },
                    header: () => "Field List",
                }}
            >
                <Route
                    path="edit/:fieldId"
                    element={<Field />}
                    loader={FieldLoader}
                    handle={{
                        crumb: ({ fieldId }: { fieldId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/field-list/edit/${fieldId}`}
                                >
                                    Edit Field
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="add"
                    element={<Field />}
                    loader={FieldLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/league-management/field-list/add`}>
                                    Add Field
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="looking-for-players"
                element={<LookingForPlayers />}
                loader={LookingForPlayersLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/looking-for-players`}>
                                Looking For Players Admin
                            </Link>
                        );
                    },
                    header: () => "Looking for Players Admin",
                }}
            >
                <Route
                    path=":teamId"
                    element={<ClubhouseLookingForPlayers />}
                    loader={LFPLoader}
                    handle={{
                        crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                            return (
                                <Link
                                    to={`/league-management/looking-for-players/${teamId}`}
                                >
                                    Looking for Players
                                </Link>
                            );
                        },
                    }}
                />
            </Route>
            <Route
                path="tryouts"
                element={<Tryouts />}
                loader={TryoutsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/tryouts`}>
                                Tryouts Admin
                            </Link>
                        );
                    },
                    header: () => "Tryouts Admin",
                }}
            >
                <Route
                    path=":teamId"
                    element={<ClubhouseTryouts />}
                    loader={ClubhouseTryoutsLoader}
                    handle={{
                        crumb: ({ teamId }: { teamId: ITeam["id"] }) => {
                            return (
                                <Link
                                    to={`/league-management/tryouts/${teamId}`}
                                >
                                    Tryouts Info
                                </Link>
                            );
                        },
                    }}
                />
            </Route>
            <Route path="ratings">
                <Route
                    path="coach"
                    element={<CoachRatings />}
                    loader={CoachRatingsLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/league-management/ratings/coach`}>
                                    Coaching/Team Ratings
                                </Link>
                            );
                        },
                        header: () => "Coaching/Team Ratings",
                    }}
                />
                <Route
                    path="umpire"
                    element={<UmpireRatings />}
                    loader={UmpireRatingsLoader}
                    handle={{
                        crumb: () => {
                            return (
                                <Link to={`/league-management/ratings/umpire`}>
                                    Umpire Ratings
                                </Link>
                            );
                        },
                        header: () => "Umpire Ratings",
                    }}
                />
            </Route>
            <Route
                path="league-registrations"
                element={<LeagueRegistrations />}
                loader={LeagueRegistrationsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link
                                to={`/league-management/league-registrations`}
                            >
                                League Registrations
                            </Link>
                        );
                    },
                    header: () => "League Registrations",
                }}
            >
                <Route
                    path=":orderId"
                    element={<Order />}
                    loader={OrderLoader}
                    handle={{
                        crumb: ({ orderId }: { orderId: IOrder["id"] }) => {
                            return (
                                <Link
                                    to={`/league-management/league-registrations/${orderId}`}
                                >
                                    Order #{`${orderId}`}
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                ></Route>
            </Route>
            <Route
                path="game-logs"
                element={<GameLogs />}
                loader={GameLogsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/game-logs`}>
                                Game Logs
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            />
            <Route
                path="game-logs/:gameId"
                element={<GameLogs />}
                loader={GameLogsLoader}
                handle={{
                    crumb: ({ gameId }: { gameId: string }) => {
                        return (
                            <Link to={`/league-management/game-logs/${gameId}`}>
                                Game Log
                            </Link>
                        );
                    },
                    header: ({ header }: { header: string }) => header,
                }}
            />
            <Route
                path="coiApproval"
                element={<COIApproval />}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/coiApproval`}>
                                COI Approvals
                            </Link>
                        );
                    },
                    header: () => "COI Approvals",
                }}
            >
                <Route
                    path=":teamId"
                    element={<COI />}
                    loader={COILoader}
                    handle={{
                        crumb: ({ teamId }: { teamId: string }) => {
                            return (
                                <Link
                                    to={`/league-management/coiApproval/${teamId}`}
                                >
                                    Approve Team COI
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="products"
                element={<ProductsIndex />}
                loader={ProductsLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/products`}>
                                Products
                            </Link>
                        );
                    },
                    header: () => "Product Management",
                }}
            >
                <Route
                    path="add"
                    element={<Product />}
                    loader={ProductLoader}
                    handle={{
                        crumb: ({
                            productId,
                        }: {
                            productId: IProduct["id"];
                        }) => {
                            return (
                                <Link to={`/league-management/products/add`}>
                                    Add Product
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="edit/:productId"
                    element={<Product />}
                    loader={ProductLoader}
                    handle={{
                        crumb: ({
                            productId,
                        }: {
                            productId: IProduct["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/league-management/products/edit/${productId}`}
                                >
                                    Edit Product
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="promo-codes"
                element={<PromoCodes />}
                loader={PromoCodesLoader}
                handle={{
                    crumb: () => {
                        return (
                            <Link to={`/league-management/promo-codes`}>
                                Promo Codes
                            </Link>
                        );
                    },
                    header: () => "Promo Code Management",
                }}
            >
                <Route
                    path="add"
                    element={<PromoCode />}
                    loader={PromoCodeLoader}
                    handle={{
                        crumb: ({
                            promoCodeId,
                        }: {
                            promoCodeId: IPromoCode["id"];
                        }) => {
                            return (
                                <Link to={`/league-management/promo-codes/add`}>
                                    Add Promo Code
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
                <Route
                    path="edit/:promoCodeId"
                    element={<PromoCode />}
                    loader={PromoCodeLoader}
                    handle={{
                        crumb: ({
                            promoCodeId,
                        }: {
                            promoCodeId: IPromoCode["id"];
                        }) => {
                            return (
                                <Link
                                    to={`/league-management/promo-codes/edit/${promoCodeId}`}
                                >
                                    Edit Promo Code
                                </Link>
                            );
                        },
                        header: ({ header }: { header: string }) => header,
                    }}
                />
            </Route>
            <Route
                path="*"
                element={<NotFound parent="leaguemanagement" />}
                handle={{ header: () => "League Management Page Not Found" }}
            />
        </Route>
    </Route>
);
